import { G } from '@mobily/ts-belt';
import DocumentsIcon from '@mui/icons-material/Description';
import DetailsIcon from '@mui/icons-material/Info';
import DashboardIcon from '@mui/icons-material/InsertChart';
import PayoutsIcon from '@mui/icons-material/Payments';
import { Typography } from '@mui/material';
import { isAfter, isBefore } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import {
  BooleanField,
  NumberField,
  RaRecord,
  ReferenceManyField,
  SelectField,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  WithRecord,
} from 'react-admin';

import {
  B2bCampaignStatus,
  GetB2bCampaignAllocationsQuery,
  GetB2bCampaignOneResponse,
  GetCampaignsEmployeesAwaitingResponseEndpoint,
  getB2bCampaignsEmployeesAwaitingResponseSortColumns,
} from '@Common/types/bo/b2b/campaigns';

import { contractCampaignActionHandlerChoices } from '@sh4res/bo-common/choices/contracts';
import {
  CancelButton,
  DatagridTyped,
  Pagination,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import { B2bCampaignFundRequestActions } from '@sh4res/bo-common/components/B2bCampaigns/B2bCampaignFundRequestActions';
import { B2bCampaignPaymentSection } from '@sh4res/bo-common/components/B2bCampaigns/B2bCampaignPaymentSection';
import { B2bCampaignsEdit } from '@sh4res/bo-common/components/B2bCampaigns/B2bCampaignsEdit';
import { CampaignStatusField } from '@sh4res/bo-common/components/B2bCampaigns/CampaignStatusField';
import {
  b2bCampaignReasonChoices,
  b2bCampaignTypeChoices,
  valueSharingBonusFiscalRuleChoices,
} from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { MatchingContributionRelatedCashSourceField } from '@sh4res/bo-common/components/B2bCampaigns/MatchingContributionRelatedCashSourceField';
import { B2bCampaignDocumentsTab } from '@sh4res/bo-common/components/B2bCampaigns/show/components/B2bCampaignDocumentsTab';
import { B2bCampaignDashboard } from '@sh4res/bo-common/components/B2bCampaigns/show/components/dashboard/B2bCampaignDashboard';
import { CampaignAllocationSection } from '@sh4res/bo-common/components/B2bCampaigns/show/components/details/components/CampaignAllocationsSection';
import { B2bCampaignPayoutsTab } from '@sh4res/bo-common/components/B2bCampaigns/show/components/payouts/B2bCampaignPayoutsTab';
import {
  CurrencyField,
  IdArrayField,
  IdField,
  ProductsField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import {
  ResourceBasePath,
  useCanAccess,
  useConfirmDialog,
  useDeletePromise,
  useResourceBasePath,
} from '@sh4res/bo-common/hooks';
import { ColumnList, getErrorMessage, RaSort, Resources } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bCampaignsShow';

const campaignEmployeesAwaitingResponseListColumns: (
  resourceBasePath: ResourceBasePath,
) => ColumnList<GetCampaignsEmployeesAwaitingResponseEndpoint['Client']['ResponseBody'][number]> = (
  resourceBasePath,
) => ({
  id: (
    <IdField
      reference={`${resourceBasePath}employees`}
      data-testid={`${testId}_AwaitingResponseIdField`}
    />
  ),
  b2b_campaign_id: (
    <IdField
      reference={`${resourceBasePath}campaigns`}
      data-testid={`${testId}_AwaitingResponseCampaignIdField`}
    />
  ),
  company_id: (
    <IdField
      reference={`${resourceBasePath}companies`}
      data-testid={`${testId}_AwaitingResponseCompanyIdField`}
    />
  ),
  firstname: <TextField data-testid={`${testId}_AwaitingResponseFirstnameField`} />,
  lastname: <TextField data-testid={`${testId}_AwaitingResponseLastnameField`} />,
});

const employeesAwaitingResponseDefaultSort: RaSort<GetB2bCampaignAllocationsQuery> = {
  field: 'id',
  order: 'ASC',
};

const endedCampaignStatuses: B2bCampaignStatus[] = [
  'campaign_ended',
  'company_funds_await',
  'company_funds_received',
  'finished',
];

const DeleteButton = ({ resource }: { resource: Resources; 'data-testid'?: string }) => {
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const resourceBasePath = useResourceBasePath();
  const notify = useNotify();

  const refresh = useRefresh();
  const [deleteOne] = useDeletePromise();

  const isEndedCampaign = useMemo(
    (): boolean => endedCampaignStatuses.includes(record?.status as B2bCampaignStatus),
    [record],
  );

  const confirmDialog = useConfirmDialog({
    title: 'resources.bo_common.b2b.campaigns.actions.delete.confirm_title',
    content: isEndedCampaign
      ? 'resources.bo_common.b2b.campaigns.actions.delete.confirm_cancel_ended_campaign'
      : 'resources.bo_common.b2b.campaigns.actions.delete.confirm_content',
    showCancelX: true,
  });

  const handleDelete = useCallback(
    async (event: React.SyntheticEvent) => {
      event.stopPropagation();

      const confirmed = await confirmDialog.confirm();
      if (!confirmed) {
        return;
      }

      try {
        await deleteOne(resource, {
          id: record?.id as number,
          previousData: record,
        });

        notify('resources.bo_common.b2b.campaigns.actions.delete.success', { type: 'success' });
        refresh();
      } catch (error: unknown) {
        notify('resources.bo_common.b2b.campaigns.actions.delete.failure', {
          type: 'warning',
          messageArgs: { error: getErrorMessage(error) },
          multiLine: true,
        });
      }
    },
    [confirmDialog, deleteOne, notify, record, resource, refresh],
  );

  const disabled = useMemo((): boolean => {
    if (!record || record.is_cancelled) {
      return true;
    }
    if (record.is_draft) {
      // always allow cancelling when draft
      return false;
    }
    // allow cancelling when campaign campaign choices are collected via app and campaign  consultation has not yet finished
    if (
      record.choice_collected_by === 'distributor' &&
      record.end_date &&
      isBefore(new Date(), record.end_date)
    ) {
      return false;
    }

    if (isEndedCampaign) {
      return false;
    }

    // disable otherwise
    return true;
  }, [record, isEndedCampaign]);

  if (!record) {
    return;
  }

  return (
    <>
      <CancelButton
        onClick={handleDelete}
        label={'resources.bo_common.b2b.campaigns.actions.delete.label'}
        permissions={{
          resource: `${resourceBasePath}campaigns`,
          action: 'delete',
        }}
        disabled={disabled}
        data-testid="BoExternalDeleteButton_DeleteButton"
      />
      {confirmDialog.Component}
    </>
  );
};

type B2bCampaignsShowProps = {
  id?: number;
  showFundRequestActions?: boolean;
  navigateTo?: (record: RaRecord) => string | -1;
};

export const B2bCampaignsShow = ({
  id,
  showFundRequestActions = true,
  navigateTo = () => -1,
}: B2bCampaignsShowProps) => {
  const resourceBasePath = useResourceBasePath();
  const resource: Resources = `${resourceBasePath}campaigns`;

  const canListAllocations = useCanAccess({
    resource: `${resourceBasePath}campaigns/allocations`,
    action: 'list',
  });
  const canListEmployeesAwaitingResponse = useCanAccess({
    resource: `${resourceBasePath}campaigns/employees_awaiting_response`,
    action: 'list',
  });
  const canListPayouts = useCanAccess({
    resource: `${resourceBasePath}campaigns/payouts`,
    action: 'list',
  });

  const campaignEmployeesAwaitingResponseRegistry = useMemo(
    () => campaignEmployeesAwaitingResponseListColumns(resourceBasePath),
    [resourceBasePath],
  );
  return (
    <ShowWithNavigation
      id={id}
      logs={
        resourceBasePath
          ? {
              resource: 'b2b_campaign',
            }
          : undefined
      }
      comments={resourceBasePath ? { source: 'id', target: 'b2b_campaign_id' } : undefined}
      navigateTo={navigateTo}
      actions={false}
      title={
        <SelectField
          sx={{ margin: 0, fontWeight: 600, fontSize: '17px', lineHeight: '1.3' }}
          source="type"
          choices={b2bCampaignTypeChoices}
          data-testid={`${testId}_TitleTypeField`}
        />
      }
      titleActions={
        <>
          {showFundRequestActions && <B2bCampaignFundRequestActions />}
          <DeleteButton resource={resource} data-testid={`${testId}_DeleteButton`} />
        </>
      }>
      <VerticalTabsLayout>
        <VerticalTab
          icon={<DashboardIcon />}
          sx={{ textTransform: 'none' }}
          data-testid={`${testId}_DashboardTab`}
          label="resources.bo_common.b2b.campaigns.tabs.dashboard.label"
          path="dashboard">
          <B2bCampaignDashboard resource={resource} />
        </VerticalTab>
        <VerticalTab
          icon={<DetailsIcon />}
          sx={{ textTransform: 'none' }}
          data-testid={`${testId}_DetailsTab`}
          label="resources.bo_common.b2b.campaigns.tabs.details.label"
          path="details"
          button={<B2bCampaignsEdit resource={resource} />}>
          <WithRecord
            render={(record: GetB2bCampaignOneResponse) => {
              const consultationFields =
                record.choice_collected_by === 'distributor'
                  ? {
                      start_date: <UTCDateField data-testid={`${testId}_StartDateField`} />,
                      end_date: <UTCDateField data-testid={`${testId}_EndDateField`} />,
                    }
                  : {};

              const isTotalGrossAmountDisplayed = record.type !== 'value_sharing_bonus';
              const isVoluntaryDepositCampaign = record.type === 'voluntary_deposit';
              const isCETCampaign = record.type === 'unused_vacation_days';

              const fields = {
                id: <IdField reference={resource} data-testid={`${testId}_IdField`} />,
                company_id: (
                  <IdField
                    reference={`${resourceBasePath}companies`}
                    data-testid={`${testId}_CompanyIdField`}
                  />
                ),
                b2b_contract_ids: (
                  <IdArrayField
                    source="b2b_contract_ids"
                    reference={`${resourceBasePath}contracts`}
                    testId={`${testId}_B2bContractIdsField`}
                  />
                ),
                products: <ProductsField data-testid={`${testId}_ProductsField`} />,
                type: (
                  <SelectChipField
                    choices={b2bCampaignTypeChoices}
                    data-testid={`${testId}_TypeField`}
                  />
                ),
                value_sharing_bonus_fiscal_rule:
                  record.type === 'value_sharing_bonus' ? (
                    <SelectChipField
                      choices={valueSharingBonusFiscalRuleChoices}
                      data-testid={`${testId}_ValueSharingBonusFiscalRuleField`}
                    />
                  ) : undefined,
                reason: (
                  <SelectChipField
                    choices={b2bCampaignReasonChoices}
                    data-testid={`${testId}_ReasonField`}
                  />
                ),
                reference_year: G.isNotNullable(record.reference_year) ? (
                  <NumberField
                    data-testid={`${testId}_ReferenceYearField`}
                    options={{ useGrouping: false }}
                  />
                ) : undefined,
                matching_contribution_related_cash_source:
                  record.type === 'matching_contribution' ? (
                    <MatchingContributionRelatedCashSourceField
                      data-testid={`${testId}_MatchingContributionRelatedCashSourceField`}
                    />
                  ) : undefined,
                related_campaign_id: G.isNotNullable(record.related_campaign_id) ? (
                  <IdField reference={resource} data-testid={`${testId}_RelatedCampaignIdField`} />
                ) : undefined,
                status: <CampaignStatusField source="status" data-testid={testId} />,
                ...consultationFields,
                fiscal_period_start: !isVoluntaryDepositCampaign ? (
                  <UTCDateField data-testid={`${testId}_FiscalPeriodStartField`} />
                ) : undefined,
                fiscal_period_end: !isVoluntaryDepositCampaign ? (
                  <UTCDateField data-testid={`${testId}_FiscalPeriodEndField`} />
                ) : undefined,
                employees_count: <NumberField data-testid={`${testId}_EmployeesCountField`} />,
                total_campaign_amount_gross: isTotalGrossAmountDisplayed ? (
                  <CurrencyField
                    currency="EUR"
                    testId={`${testId}_TotalCampaignAmountGrossField`}
                  />
                ) : undefined,
                total_campaign_amount_net: (
                  <CurrencyField currency="EUR" testId={`${testId}_TotalCampaignAmountNetField`} />
                ),
                total_entry_fee_amount: (
                  <CurrencyField currency="EUR" testId={`${testId}_TotalEntryFeeAmountField`} />
                ),
                total_net_invested_amount: (
                  <CurrencyField currency="EUR" testId={`${testId}_TotalNetInvestedAmountField`} />
                ),
                total_gross_amount: isTotalGrossAmountDisplayed ? (
                  <CurrencyField currency="EUR" testId={`${testId}_TotalGrossAmountField`} />
                ) : undefined,
                total_net_amount: (
                  <CurrencyField currency="EUR" testId={`${testId}_TotalNetAmountField`} />
                ),
                group_operation_gross_amount: (
                  <CurrencyField
                    currency="EUR"
                    testId={`${testId}_GroupOperationGrossAmountField`}
                  />
                ),
                total_matching_contribution_amount: (
                  <TotalMatchingContributionField
                    source="total_matching_contribution_amount"
                    data-testid={`${testId}_TotalMatchingContributionAmountField`}
                  />
                ),
                total_matching_contribution_amount_gross: (
                  <TotalMatchingContributionField
                    source="total_matching_contribution_amount_gross"
                    data-testid={`${testId}_TotalMatchingContributionAmountGrossField`}
                  />
                ),
                total_matching_contribution_entry_fee_amount: (
                  <TotalMatchingContributionField
                    source="total_matching_contribution_entry_fee_amount"
                    data-testid={`${testId}_TotalMatchingContributionEntryFeeAmountField`}
                  />
                ),
                total_matching_contribution_net_invested_amount: (
                  <TotalMatchingContributionField
                    source="total_matching_contribution_net_invested_amount"
                    data-testid={`${testId}_TotalMatchingContributionNetInvestedAmountField`}
                  />
                ),
                is_late_payment_interests:
                  !isVoluntaryDepositCampaign && !isCETCampaign ? (
                    <BooleanField data-testid={`${testId}_IsLatePaymentInterestsField`} />
                  ) : undefined,
                late_payment_interests_override_amount:
                  !isVoluntaryDepositCampaign && !isCETCampaign ? (
                    <CurrencyField
                      currency="EUR"
                      testId={`${testId}_LatePaymentInterestsOverrideAmountField`}
                    />
                  ) : undefined,
                created_at: (
                  <UTCDateField
                    showTime={!isBoExternal()}
                    data-testid={`${testId}_CreatedAtField`}
                  />
                ),
                updated_at: (
                  <UTCDateField
                    showTime={!isBoExternal()}
                    data-testid={`${testId}_UpdatedAtField`}
                  />
                ),
              };

              return (
                <ShowSection label="general.sections.details">
                  <ShowFields<GetB2bCampaignOneResponse> fields={fields} />
                </ShowSection>
              );
            }}
          />
          <WithRecord
            render={(record: GetB2bCampaignOneResponse) =>
              record.type !== 'voluntary_deposit' &&
              record.type !== 'matching_contribution' && (
                <ShowSection label="resources.bo_common.b2b.campaigns.sections.general.processing">
                  <ShowFields<GetB2bCampaignOneResponse>
                    fields={{
                      allocation_computed_by: (
                        <SelectChipField
                          choices={contractCampaignActionHandlerChoices}
                          data-testid={`${testId}_AllocationComputedByField`}
                        />
                      ),
                      choice_collected_by: (
                        <SelectChipField
                          choices={contractCampaignActionHandlerChoices}
                          data-testid={`${testId}_ChoiceCollectedByField`}
                        />
                      ),
                      payout_by: (
                        <SelectChipField
                          choices={contractCampaignActionHandlerChoices}
                          data-testid={`${testId}_PayoutByField`}
                        />
                      ),
                      matching_contribution_computed_by: (
                        <SelectChipField
                          choices={contractCampaignActionHandlerChoices}
                          data-testid={`${testId}_MatchingContributionComputedByField`}
                        />
                      ),
                    }}
                  />
                </ShowSection>
              )
            }
          />
          <B2bCampaignPaymentSection resourceBasePath={resourceBasePath} />
          {canListAllocations && <CampaignAllocationSection />}
          <WithRecord
            render={(record: GetB2bCampaignOneResponse) =>
              canListEmployeesAwaitingResponse && record.choice_collected_by === 'distributor' ? (
                <ShowSection label="resources.bo_common.b2b.campaigns.employees_awaiting_response.label">
                  <ReferenceManyField
                    reference={`${resourceBasePath}campaigns/employees_awaiting_response`}
                    target="b2b_campaign_id"
                    perPage={10}
                    pagination={<Pagination />}
                    sort={employeesAwaitingResponseDefaultSort}
                    data-testid={`${testId}_EmployeesAwaitingResponseField`}>
                    <DatagridTyped<
                      GetCampaignsEmployeesAwaitingResponseEndpoint['Client']['ResponseBody'][number]
                    >
                      sortableColumns={getB2bCampaignsEmployeesAwaitingResponseSortColumns}
                      columnRegistry={campaignEmployeesAwaitingResponseRegistry}
                      displayedColumns={['id', 'firstname', 'lastname']}
                      bulkActionButtons={false}
                      showCard={true}
                    />
                  </ReferenceManyField>
                </ShowSection>
              ) : undefined
            }
          />
        </VerticalTab>
        {!isBoExternal() && (
          <VerticalTab
            icon={<DocumentsIcon />}
            sx={{ textTransform: 'none' }}
            data-testid={`${testId}_DocumentsTab`}
            label="resources.bo_common.b2b.campaigns.tabs.documents.label"
            path="documents">
            <B2bCampaignDocumentsTab />
          </VerticalTab>
        )}
        {!isBoExternal() && canListPayouts && (
          <VerticalTab
            icon={<PayoutsIcon />}
            sx={{ textTransform: 'none' }}
            data-testid={`${testId}_PayoutsTab`}
            label="resources.bo_common.b2b.campaigns.tabs.payouts.label"
            path="payouts">
            <B2bCampaignPayoutsTab />
          </VerticalTab>
        )}
      </VerticalTabsLayout>
    </ShowWithNavigation>
  );
};

const TotalMatchingContributionField = ({ source }: { source: string; 'data-testid'?: string }) => {
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const translate = useTranslate();

  if (!record) {
    return null;
  }

  if (
    record.choice_collected_by === 'distributor' &&
    record.end_date &&
    isAfter(record.end_date, new Date())
  ) {
    return (
      <Typography>
        {translate(
          'resources.bo_common.b2b.campaigns.sections.confirmation.matching_contributions_to_be_value',
        )}
      </Typography>
    );
  }

  return (
    <CurrencyField
      source={source}
      currency="eur"
      testId="BoExternalTotalMatchingContributionField_CurrencyField"
    />
  );
};
